import { EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InfoCallout } from '@/components/ui/info-callout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useValidateIntegration } from '@/hooks/useValidateIntegration';

import { useDeleteMasterKey, useSetMasterKey } from './hooks/useMasterKey';

/**
 * Shared config dialog for a single Global OSINT credential source (Hunter.io /
 * Lusha / Apollo / Dehashed). Masked key field + inline validation error +
 * Save/Clear footer, wired to the enrichment master-key store
 * (`PUT`/`DELETE /enrichment/{plugin}/key`) via `useSetMasterKey` /
 * `useDeleteMasterKey`. Deliberately has NO enable/disable toggle and NO credits
 * line (for these creds, configured == active).
 *
 * The brand logo is a flat, theme-agnostic PNG at `/icons/{id}.png`; on load
 * error it falls back to the descriptor's lucide `Icon`.
 *
 * The enrichment key endpoint (`PUT /enrichment/{plugin}/key`) does NOT validate
 * on set, so Save first validates the key via `/integration/validate` (which
 * runs the per-plugin server-side validator, e.g. Hunter.io rejects a bad key)
 * and only stores it when validation passes. Any validation error surfaces
 * through the inline error callout, mirroring `PluginConfigDialog`.
 */

export interface InfraCredentialDialogSource {
  /**
   * Plugin id used for the `/enrichment/{id}/key` routes. NOTE: Apollo's id is
   * `pius_apollo` (it reuses the existing pius credential) even though its
   * label is "Apollo".
   */
  id: string;
  label: string;
  Icon: LucideIcon;
  /** Whether a key is already stored (drives the Clear action + status line). */
  configured: boolean;
}

interface InfraCredentialConfigDialogProps {
  source: InfraCredentialDialogSource | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * The `@/utils/api` mutation wrapper packs the backend message into `Error.name`
 * (via `getQueryError` when `getErrorFromResponse` is set) and a support hint
 * into `Error.message`. Prefer the backend title so the real validation reason
 * (e.g. a rejected Hunter.io key) is shown rather than swallowed.
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name && error.name !== 'Error') return error.name;
    if (error.message) return error.message;
  }
  return 'Failed to save credential. Please try again.';
}

export function InfraCredentialConfigDialog({
  source,
  onOpenChange,
}: InfraCredentialConfigDialogProps) {
  const [value, setValue] = useState('');
  const [visible, setVisible] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [iconError, setIconError] = useState(false);

  const { mutate: setMasterKey, isPending: isSaving } = useSetMasterKey();
  const { mutate: deleteMasterKey, isPending: isDeleting } =
    useDeleteMasterKey();
  const { validateIntegration, isValidating } = useValidateIntegration();

  const isPending = isSaving || isDeleting || isValidating;
  const trimmed = value.trim();
  const Icon = source?.Icon;
  const inputId = source ? `infra-credential-${source.id}` : 'infra-credential';

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setValue('');
      setVisible(false);
      setValidationError(null);
    }
    onOpenChange(open);
  };

  const handleSave = async () => {
    if (!source || !trimmed) return;
    setValidationError(null);

    // The enrichment key store does not validate on set, so validate the key
    // server-side first and only persist it when valid.
    const { isValid, validationErrors } = await validateIntegration({
      provider: source.id,
      config: { api_key: trimmed },
      suppressErrorToast: true,
    });
    if (!isValid) {
      const firstError = validationErrors?.[0];
      setValidationError(
        firstError
          ? `${firstError.title}: ${firstError.message}`
          : 'Validation failed. Please check your credentials.'
      );
      return;
    }

    setMasterKey(
      { plugin: source.id, apiKey: trimmed },
      {
        onSuccess: () => handleOpenChange(false),
        onError: error => setValidationError(getErrorMessage(error)),
      }
    );
  };

  const handleClear = () => {
    if (!source) return;
    setValidationError(null);
    deleteMasterKey(
      { plugin: source.id },
      {
        onSuccess: () => handleOpenChange(false),
        onError: error => setValidationError(getErrorMessage(error)),
      }
    );
  };

  return (
    <Dialog open={!!source} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {source &&
              (iconError ? (
                Icon && <Icon className="text-chariot-text-secondary size-5" />
              ) : (
                <img
                  src={`/icons/${source.id}.png`}
                  alt=""
                  className="size-5"
                  onError={() => setIconError(true)}
                />
              ))}
            Configure {source?.label}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* API key field */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor={inputId}
              className="text-chariot-text-secondary text-xs font-medium"
            >
              API Key<span className="text-error ml-0.5">*</span>
            </Label>
            <div className="relative">
              <Input
                id={inputId}
                type={visible ? 'text' : 'password'}
                autoComplete="off"
                placeholder={
                  source?.configured
                    ? 'Enter a new key to replace'
                    : 'Enter API key'
                }
                value={value}
                onChange={event => {
                  setValue(event.target.value);
                  setValidationError(null);
                }}
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setVisible(prev => !prev)}
                className="text-chariot-text-tertiary hover:text-chariot-text-secondary absolute top-1/2 right-3 -translate-y-1/2"
                aria-label={visible ? 'Hide field' : 'Show field'}
              >
                {visible ? (
                  <EyeSlashIcon className="size-4" />
                ) : (
                  <EyeIcon className="size-4" />
                )}
              </button>
            </div>
          </div>

          {/* Backend validation error */}
          {validationError && (
            <InfoCallout variant="error" role="alert" aria-live="assertive">
              {validationError}
            </InfoCallout>
          )}

          {/* Current key status */}
          <div className="flex items-center gap-2">
            <KeyIcon className="text-chariot-text-tertiary size-4 shrink-0" />
            <span className="text-chariot-text-secondary text-xs">
              {source?.configured
                ? 'An API key is configured'
                : 'No API key configured'}
            </span>
          </div>
        </div>

        <DialogFooter className="flex-row items-center">
          {source?.configured && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={isPending}
              className="text-error hover:text-error mr-auto"
            >
              {isDeleting ? 'Removing...' : 'Clear Key'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isPending || !trimmed}
          >
            {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
